"use client";

import React, { useContext } from "react";
import dayjs from "dayjs";
import ChartHeader from "../../../components/common/ChartHeader";
import { LineCard, BarCard } from "../../../components/charts";
import { ServiceContext } from "../../../hooks/context/ServiceContext";

interface ChartSectionProps {
  chartData: any;
  selectedValues: string[];
}


// BarSeries type definition
interface BarSeries {
  name: string;
  data: number[];
}

const ChartSection: React.FC<ChartSectionProps> = ({ chartData, selectedValues }) => {
const { service: network } = useContext(ServiceContext);
const trends = chartData?.data?.userActivityTrends || [];
console.log("trends", trends);
console.log("network in ChartSection:", network);

  let formattedData: any[] = [];
  
  if (network === 'mtn') {
    // ✅ MTN Format: Array of objects with period, dailyUsers, totalSessions, avgScore
    formattedData = Array.isArray(trends) ? trends.map((item: any) => ({
      period: item.period,
      dailyUsers: item.dailyUsers,
      totalSessions: item.totalSessions,
      avgScore: item.avgScore,
      date: dayjs(item.period).format("MMM D")
    })) : [];
  } else {
    // ✅ GLO Format: Object with data array containing date, period, actions
    const trendData = trends?.data || [];
    
    // Flatten user actions across all dates
    const allActions: string[] = [
      ...new Set(trendData.flatMap((d: any) => d.actions.map((a: any) => a.name)) as string[]),
    ];

    // Build dataset per date for all actions
    formattedData = trendData?.map((day: any) => {
      const entry: Record<string, any> = { date: day.date, period: day.period };
      allActions.forEach((action) => {
        const actionItem = day.actions.find((a: any) => a.name === action);
        entry[action as string] = actionItem ? actionItem.count : 0;
      });
      return entry;
    }) || [];
  }

  let dailyUsersLineSeries: any[] = [];
  let totalSessionsLineSeries: any[] = [];
  let avgScoreLineSeries: any[] = [];
  let barSeries: any[] = [];
  let xAxis: string[] = [];

  if (network === 'mtn') {
    // ✅ MTN Format: Prepare data for MTN metrics
    dailyUsersLineSeries = [
      {
        name: "Daily Users",
        data: formattedData.map((d: any) => d.dailyUsers),
      },
    ];

    totalSessionsLineSeries = [
      {
        name: "Total Sessions",
        data: formattedData.map((d: any) => d.totalSessions),
      },
    ];

    avgScoreLineSeries = [
      {
        name: "Average Score",
        data: formattedData.map((d: any) => d.avgScore),
      },
    ];

    barSeries = [
      {
        name: "Daily Users",
        data: formattedData.map((d: any) => d.dailyUsers),
      },
      {
        name: "Total Sessions",
        data: formattedData.map((d: any) => d.totalSessions),
      },
    ];

    xAxis = formattedData.map((d: any) => d.date);
  } else {
    // ✅ GLO Format: Prepare data for GLO actions
    const allActions: string[] = [
      ...new Set(formattedData.flatMap((d: any) => Object.keys(d).filter(key => key !== 'date' && key !== 'period'))),
    ];

    // Prepare data for BarCard (for user activity breakdown)
    barSeries = allActions.map((action) => ({
      name: action,
      data: formattedData.map((d: any) => d[action as string] || 0),
    }));

    // Prepare data for LineCard (for user growth trends)
    dailyUsersLineSeries = [
      {
        name: "New Sub",
        data: formattedData.map((d: any) => d["New Sub"] ?? 0),
      },
      {
        name: "Renewal",
        data: formattedData.map((d: any) => d["Renewal"] ?? 0),
      },
      {
        name: "UnSub",
        data: formattedData.map((d: any) => d["UnSub"] ?? 0),
      },
    ];

    xAxis = formattedData.map((d: any) => d.date);
  }

  return (
    <div className="mt-10 space-y-8">
      {/* ✅ Line Chart for Growth/Subscription Trends */}
      {selectedValues.includes("userGrowth") && (
        <div className="chart-container">
          <ChartHeader 
            title={network === 'mtn' ? "Daily Users Trend" : "Subscription Trends"} 
            subtitle={network === 'mtn' ? "User activity over time" : "New vs Renewal vs Unsub"} 
          />
          <LineCard
            title={network === 'mtn' ? "Daily Users" : "Subscription Activity"}
            yAxisLabel={network === 'mtn' ? "Users" : "Count"}
            xAxisLabel="Period"
            xAxis={xAxis}
            series={dailyUsersLineSeries}
            colors={network === 'mtn' ? ["#061638"] : ["#061638", "#FADFD7", "#828A9B"]}
          />
        </div>
      )}

      {/* ✅ Line Chart for Sessions (MTN only) */}
      {network === 'mtn' && selectedValues.includes("engagementMetrics") && (
        <div className="chart-container">
          <ChartHeader title="Total Sessions Trend" subtitle="Session activity over time" />
          <LineCard
            title="Total Sessions"
            yAxisLabel="Sessions"
            xAxisLabel="Period"
            xAxis={xAxis}
            series={totalSessionsLineSeries}
            colors={["#FADFD7"]}
          />
        </div>
      )}

      {/* ✅ Line Chart for Average Score (MTN only) */}
      {network === 'mtn' && selectedValues.includes("retentionAnalysis") && (
        <div className="chart-container">
          <ChartHeader title="Average Score Trend" subtitle="Performance metrics over time" />
          <LineCard
            title="Average Score"
            yAxisLabel="Score"
            xAxisLabel="Period"
            xAxis={xAxis}
            series={avgScoreLineSeries}
            colors={["#828A9B"]}
          />
        </div>
      )}

      {/* ✅ Bar Chart for Activity Metrics */}
      {selectedValues.includes("activityBreakdown") && (
        <div className="chart-container">
          <ChartHeader 
            title="User Activity Breakdown" 
            subtitle={network === 'mtn' ? "Daily users vs total sessions" : "All user actions"} 
          />
          <BarCard
            title="Activity Overview"
            yAxisLabel="Count"
            xAxisLabel="Period"
            xAxis={xAxis}
            series={barSeries}
            colors={network === 'mtn' ? ["#061638", "#FADFD7"] : ["#061638", "#FADFD7", "#828A9B", "#52c41a", "#faad14", "#ff4d4f"]}
          />
        </div>
      )}
    </div>
  );
};

export default ChartSection;
